import { useAuth0 } from '@auth0/auth0-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type AxiosError } from 'axios'

import {
  activateAccountMutation,
  getMyselfOptions,
  getMyselfQueryKey,
  getUserOrganizationsOptions,
  refreshApiKeyMutation,
  resendVerificationEmailMutation,
} from '@/client/@tanstack/react-query.gen'
import { type UserSchemaPublic } from '@/client/types.gen'

// MOCKED
const getUserQueryKey = <T>(params: T) => ['users', params]

export const useMyself = (opts = {}) => {
  const { isAuthenticated, logout } = useAuth0()
  try {
    return useQuery({
      ...getMyselfOptions(),
      ...opts,
      throwOnError: true,
      enabled: isAuthenticated,
    })
  } catch (error) {
    const errorResponse = (error as AxiosError)?.response
    if (errorResponse?.status === 403) {
      logout({ logoutParams: { returnTo: window.location.origin } })
    }
    return {
      data: {} as UserSchemaPublic,
      error: error as Error,
      isLoading: false,
    }
  }
}

export const useRefreshUserApiKey = (userId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    ...refreshApiKeyMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getUserQueryKey({ path: { user_id: userId } }),
      })
      queryClient.invalidateQueries({
        queryKey: getMyselfQueryKey(),
      })
    },
  })
}

export const useUserOrganizations = (opts = {}) => {
  return useQuery({
    ...getUserOrganizationsOptions(),
    ...opts,
  })
}

export const useActivateAccount = () => {
  const queryClient = useQueryClient()
  return useMutation({
    ...activateAccountMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getMyselfQueryKey(),
      })
    },
  })
}

export const useResendVerificationEmail = () => {
  return useMutation({
    ...resendVerificationEmailMutation(),
  })
}
